import { defineAnalyticsEvent } from '@heidi/runtime/shell';

import type { DictateTourBannerCtaClickedEventProps } from '../contracts/dictate-tour-banner-analytics.types';
import type {
  DictationCsatDismissedEventProps,
  DictationCsatSubmittedEventProps,
} from '../contracts/dictation-csat-analytics.types';
import type { DictationLogsSpotlightDismissedEventProps } from '../contracts/dictation-logs-analytics.types';
import type {
  DictationCompletedEventProps,
  DictationErroredEventProps,
  DictationStartedEventProps,
} from '../contracts/dictation-session-analytics.types';

/**
 * The seven event names dictation owns, declared once each (D9 ruling 27).
 *
 * FROZEN: each `name` is a warehouse key, so a rename is a new event and a second
 * spelling splits a metric. `builds` omitted throughout = cloud-only (D6). The
 * emit and its (non-uniform) routing are app-side, keyed by these literals.
 *
 * Excluded names and the emitter measurement behind each: openspec task 2.1e,
 * asserted in `src/composition/dictation-analytics-declaration.test.ts`.
 */

// The session funnel: `dictation_id` is what makes these three one series.
export const dictationStarted = defineAnalyticsEvent<DictationStartedEventProps>()({
  name: 'universal_dictation_started',
  description: 'A dictation session began recording',
});

export const dictationCompleted = defineAnalyticsEvent<DictationCompletedEventProps>()({
  name: 'universal_dictation_completed',
  description: 'A dictation session reached its end, carrying the outcome it ended on',
});

export const dictationErrored = defineAnalyticsEvent<DictationErroredEventProps>()({
  name: 'universal_dictation_errored',
  description: 'A dictation session failed on a transcription API error',
});

// The CSAT survey's two responses. Its impression is the cross-domain
// `survey_viewed`, so there is no third descriptor here.
export const dictationCsatSubmitted =
  defineAnalyticsEvent<DictationCsatSubmittedEventProps>()({
    name: 'universal_dictation_csat_submitted',
    description: 'A star rating was submitted on the post-dictation CSAT popover',
  });

export const dictationCsatDismissed =
  defineAnalyticsEvent<DictationCsatDismissedEventProps>()({
    name: 'universal_dictation_csat_dismissed',
    description: 'The post-dictation CSAT popover was dismissed without a rating',
  });

// The /dictation-logs page's two awareness surfaces.
export const dictationLogsSpotlightDismissed =
  defineAnalyticsEvent<DictationLogsSpotlightDismissedEventProps>()({
    name: 'dictation_logs_spotlight_dismissed',
    description: 'The Dictate-history awareness spotlight was dismissed',
  });

export const dictateTourBannerCtaClicked =
  defineAnalyticsEvent<DictateTourBannerCtaClickedEventProps>()({
    name: 'dictate_tour_banner_cta_clicked',
    description: "The dictate-page onboarding tour banner's CTA was clicked",
  });
